/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable, OnDestroy } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { AngularFireDatabase } from '@angular/fire/compat/database';

import { Project, ProjectFields } from 'src/app/app-data/models/Project';
import { DefaultSplitDataRenderType } from '@touchplan/common';
import * as appStrings from '@touchplan/strings';
import { ITabDefinition } from '../interfaces/ITabDefinition';
import { countries } from 'src/app/app-data/constants/countries';
import { AppState } from 'src/app/services/app-state.service';
import { ProjectState } from 'src/app/services/project-state.service';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { Subject } from 'rxjs';
import { OrganizationFirebaseCloudFunctionsService } from 'src/app/services/firebase-cloud-functions/organization-cloud-functions.service';

import firebase from 'firebase/compat/app';

@Injectable()
export class ProjectInformationService implements OnDestroy {
	public projectType$: Observable<string[]>;
	public marketSector$: Observable<string[]>;
	public isInternal$: Observable<boolean>;

	public strings = appStrings;

	// user visible strings
	public informationTabDefs: ITabDefinition[] = [
		{ key: 'project', display: this.strings.PROJECT_PROPERTY_PROJECT },
		{ key: 'costs', display: this.strings.PROJECT_PROPERTY_COSTS },
		{ key: 'schedules', display: this.strings.PROJECT_PROPERTY_SCHEDULES },
	];
	//seperated out so it can be added and removed
	private masterScheduleTab: ITabDefinition = {
		key: 'masterSchedule',
		display: this.strings.PROJECT_PROPERTY_MASTER_SCHEDULE,
	};

	public countries = countries;

	private logger: ILoggerService;

	private shutdownSubject$ = new Subject<void>();

	constructor(
		private db: AngularFireDatabase,
		private appState: AppState,
		private projectState: ProjectState,
		private organizationFBCloudFunctionsService: OrganizationFirebaseCloudFunctionsService,
		private featureFlagManager: FeatureFlagManager
	) {
		this.logger = LoggerServiceFactory.create('ProjectInformationService');
		this.isInternal$ = this.appState.user.pipe(map((user) => user.isInternal));

		const projectTypePath = fancyFirebaseGetters.getEnumsPath('projectTypes');
		const projectTypeRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(projectTypePath);

		const marketSectorsPath = fancyFirebaseGetters.getEnumsPath('marketSectors');
		const marketSectorsRef =
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(marketSectorsPath);

		this.projectType$ = this.isInternal$.pipe(
			switchMap((internal) => {
				if (!internal) {
					return of([]);
				} else {
					return this.db.list<string>(projectTypeRef).valueChanges();
				}
			})
		);

		this.marketSector$ = this.isInternal$.pipe(
			switchMap((internal) => {
				if (!internal) {
					return of([]);
				} else {
					return this.db.list<string>(marketSectorsRef).valueChanges();
				}
			})
		);

		this.featureFlagManager
			.getFlag$(DynamicFlagTypes.TICKET_EXPORT_ENABLED)
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((flag: boolean) => {
				//...because why not just store this info in an efficiently query-able object when we can do this nonsense
				const alreadyAdded = this.getInformationTabDef('masterSchedule');

				if (flag && !alreadyAdded) {
					//make angular's change detection happy
					this.informationTabDefs = [...this.informationTabDefs, this.masterScheduleTab];
				} else if (!flag && alreadyAdded) {
					const newArray = [...this.informationTabDefs];

					//a pop would be more efficient, less future compatible tho...
					const idx = newArray.indexOf(alreadyAdded);
					newArray.splice(idx, 1);

					this.informationTabDefs = newArray;
				}
			});
	}

	//TODO - do those old tickets to make this use Project...
	public getProjectMetrics(project: ProjectFields): Observable<ProjectFields> {
		const path = fancyFirebaseGetters.getProjectMetricsPath(project.projectId);
		const ref = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(path, project.projectId);

		return this.db
			.object(ref)
			.valueChanges()
			.pipe(
				map((item) => {
					if (item) {
						const others = Object.getOwnPropertyNames(item);
						others.forEach((key) => {
							project[key] = item[key];
						});
					}

					return project;
				})
			);
	}

	public async updateProject(project: Project, formValues: any, organizationId?: string): Promise<void> {
		const path = fancyFirebaseGetters.getProjectMetricsPath(project.$id);
		const ref = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(path, project.$id);

		// update project metrics
		const metrics = {};
		if (project.view.costs) {
			metrics['costs'] = project.view.costs;
		}
		if (project.view.schedules) {
			metrics['schedules'] = project.view.schedules;
		}
		ref.update(metrics);

		// update project name, address
		const properties = formValues.Project;
		if (properties) {
			this.projectState.projectsApi.persistData(project, DefaultSplitDataRenderType.edit);
		}

		// if an Organization was selected, set that:
		if (organizationId) {
			this.organizationFBCloudFunctionsService
				.addProjectToOrganization(organizationId, project.$id)
				.subscribe({
					error: (e) => {
						this.logger.error(
							`Could not add project,'${project.$id}', to organization,'${organizationId}',: ${e.message}`
						);
					},
				});
		}
	}

	public assessProjects(projects: ProjectFields[], budgetThreshold = 5, scheduleThreshold = 30) {
		let maxBudget = 0;
		let minBudget = 0;

		projects.forEach((element) => {
			if (element) {
				if (element['costs'] !== undefined && Object.getOwnPropertyNames(element['costs']).length > 0) {
					const costs = element['costs'];
					const keys = Object.getOwnPropertyNames(costs).sort();
					const which = keys[keys.length - 1];
					const eac = costs[which].estimatedToComplete;
					const bac = costs[which].approved;
					const variance = eac - bac;
					element.cost = {
						eac,
						bac,
						variance,
						onBudget: Math.abs(variance / bac) <= budgetThreshold / 100 ? true : false,
					};
				} else {
					element.cost = {
						eac: undefined,
						bac: undefined,
						variance: undefined,
						onBudget: true,
					};
				}

				if (
					element['schedules'] !== undefined &&
					Object.getOwnPropertyNames(element['schedules']).length > 0
				) {
					const schedules = element['schedules'];
					const keys = Object.getOwnPropertyNames(schedules).sort();
					const which = keys[keys.length - 1];
					const efd = schedules[which].estimatedCompletion;
					const pfd = schedules[which].approvedCompletion;
					const delay = (Date.parse(efd) - Date.parse(pfd)) / (60 * 60 * 24 * 1000);
					element.schedule = {
						efd,
						pfd,
						delay,
						onSchedule: Math.abs(delay) <= scheduleThreshold ? true : false,
					};
				} else {
					element.schedule = {
						efd: '',
						pfd: '',
						delay: undefined,
						onSchedule: true,
					};
				}
			}

			if (element.cost && element.cost.eac) {
				if (element.cost.eac > maxBudget) {
					maxBudget = element.cost.eac;
				}
				if (minBudget === 0 || element.cost.eac < minBudget) {
					minBudget = element.cost.eac;
				}
			}
		});
	}

	public getScheduleDelay(diff: any): string {
		let result = '';
		const number = Number.parseInt(diff, 10);
		if (!isNaN(number)) {
			result = Math.abs(number) + ' Day ' + (number < 0 ? 'Ahead' : 'Behind');
		}
		return result;
	}

	public getInformationTabDefs(): ITabDefinition[] {
		return this.informationTabDefs;
	}

	public getInformationTabDef(key: string): ITabDefinition {
		return this.informationTabDefs.find((def) => def.key === key);
	}

	public ngOnDestroy(): void {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}
}
